/**
 * pulsebot/kbSearch.ts — deterministic knowledge-base retrieval for PulseBot.
 *
 * Phase 5: the user asks a "how do I…" question and the bot returns the best
 * matching KB article(s) with deep-links. NO LLM — plain client-side keyword
 * scoring over the (small) published-article corpus.
 *
 * Source of truth: listArticles() (lib/knowledgeBase.ts) returns published,
 * non-deleted, RLS-filtered articles — so visibility is enforced server-side and
 * this module needs no role/visibility logic. One fetch, score in memory.
 *
 * Used both by the explicit KB intent (router.ts) and the no-match fallback
 * (router.ts runPulseBot), so a natural question that doesn't start with "how"
 * (e.g. "password reset") still finds an article.
 */

import { listArticles } from '../knowledgeBase';
import type { BotResult, BotResultItem } from './types';

/** Path that deep-links straight to an article (KB page reads ?article=<id>). */
const KB_BASE = '/support/knowledge-base';

/**
 * Trigger phrases stripped from the query before tokenizing — they're how the
 * user frames the question, not what they're asking about. Longest first so
 * multi-word prefixes are removed before their single-word substrings.
 */
const STRIP_PHRASES = [
  'knowledge base', 'article about', 'article on',
  'how do i', 'how can i', 'where do i', 'how to', 'kb',
];

/** Common words that carry no retrieval signal. */
const STOPWORDS = new Set([
  'a', 'an', 'the', 'to', 'my', 'me', 'i', 'do', 'how', 'is', 'are', 'of', 'for',
  'in', 'on', 'at', 'can', 'your', 'you', 'what', 'and', 'or', 'with', 'about',
  'this', 'that', 'it', 'be', 'get', 'getting', 'need', 'want',
]);

/**
 * Turn a raw question into a set of meaningful lowercased terms: strip trigger
 * phrases, split on non-word chars, drop stopwords and sub-2-char tokens.
 */
export function extractQueryTerms(query: string): string[] {
  let q = query.toLowerCase();
  for (const phrase of STRIP_PHRASES) q = q.split(phrase).join(' ');
  const seen = new Set<string>();
  for (const tok of q.split(/\W+/)) {
    if (tok.length >= 2 && !STOPWORDS.has(tok)) seen.add(tok);
  }
  return Array.from(seen);
}

/** Build a short, single-line snippet from an article body. */
function snippet(content: string): string {
  const flat = content.replace(/\s+/g, ' ').trim();
  return flat.length > 140 ? `${flat.slice(0, 140)}…` : flat;
}

/** The browse-the-KB fallback result (no good match / empty query). */
function browseFallback(): BotResult {
  return {
    text: "I couldn't find an article on that. Try browsing the knowledge base:",
    items: [{ label: 'Browse the knowledge base', link: KB_BASE }],
  };
}

/**
 * True when a BotResult is a real KB hit (article items), not the browse-only
 * fallback. The no-match path uses this to decide whether to show KB results or
 * fall through to the generic capability fallback.
 */
export function hasKbMatches(result: BotResult): boolean {
  return (result.items?.length ?? 0) > 0 && result.items![0].link !== KB_BASE;
}

/**
 * Search the KB for the query and return the top matches (or a browse fallback).
 * Scoring per query term: title +3, tag +2, content +1 (presence, not freq).
 * Threshold: at least one title/tag hit, OR total score >= 2. Top 3 returned.
 * Never throws — a fetch failure degrades to the browse fallback.
 */
export async function searchKb(query: string): Promise<BotResult> {
  const terms = extractQueryTerms(query);
  if (terms.length === 0) return browseFallback();

  let articles;
  try {
    articles = await listArticles();
  } catch (err) {
    console.error('[PulseBot] searchKb listArticles failed', err);
    return browseFallback();
  }

  const scored = articles
    .map(a => {
      const title = (a.title ?? '').toLowerCase();
      const content = (a.content ?? '').toLowerCase();
      const tags = (a.tags ?? []).map(t => t.toLowerCase());
      let score = 0;
      let titleOrTagHit = false;
      for (const term of terms) {
        if (title.includes(term)) { score += 3; titleOrTagHit = true; }
        if (tags.some(t => t.includes(term))) { score += 2; titleOrTagHit = true; }
        if (content.includes(term)) { score += 1; }
      }
      return { a, score, titleOrTagHit };
    })
    .filter(s => s.score > 0 && (s.titleOrTagHit || s.score >= 2))
    .sort((x, y) => y.score - x.score)
    .slice(0, 3);

  if (scored.length === 0) return browseFallback();

  const items: BotResultItem[] = scored.map(({ a }) => ({
    label: a.title,
    sublabel: snippet(a.content ?? ''),
    badge: a.category || undefined,
    link: `${KB_BASE}?article=${a.id}`,
  }));

  return { text: "Here's what I found:", items };
}
